import React, { useState } from "react";
import { motion } from "framer-motion";
import { useLang } from "@/i18n/LanguageContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { CONTACT } from "@/constants/testIds";
import { Mail, MessageCircle, Send, Calendar, Check } from "lucide-react";

export default function Contact() {
  const { t } = useLang();
  const [form, setForm] = useState({ name: "", email: "", project_type: "", budget: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await api.post("/contact", form);
      setDone(true);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || t.contact.sent_error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="bg-[#0a0a0a] text-white py-24 lg:py-32">
      <div className="max-w-[1600px] mx-auto px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#8a8a8a] mb-3">
            · {t.contact.label}
          </div>
          <h2 className="font-mono font-bold tracking-tighter text-3xl sm:text-4xl lg:text-5xl">
            {t.contact.title}
          </h2>

          <div className="mt-10 space-y-3">
            <a
              href="mailto:hello@kuryarti.com"
              className="group flex items-center justify-between gap-4 p-4 border border-white/20 hover:border-[#00d4ff] hover:bg-white/5 transition-colors"
            >
              <div className="flex items-center gap-3">
                <Mail size={18} className="text-[#00d4ff]" />
                <span className="font-mono text-sm">hello@kuryarti.com</span>
              </div>
              <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/50 group-hover:text-[#00d4ff]">
                email
              </span>
            </a>

            <a
              href="https://t.me/kuryarti"
              target="_blank"
              rel="noreferrer"
              className="group flex items-center justify-between gap-4 p-4 border border-white/20 hover:border-[#ff006e] hover:bg-white/5 transition-colors"
            >
              <div className="flex items-center gap-3">
                <Send size={18} className="text-[#ff006e]" />
                <span className="font-mono text-sm">@kuryarti</span>
              </div>
              <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/50 group-hover:text-[#ff006e]">
                telegram
              </span>
            </a>

            <a
              href="https://wa.me/77000000000"
              target="_blank"
              rel="noreferrer"
              className="group flex items-center justify-between gap-4 p-4 border border-white/20 hover:border-[#00d4ff] hover:bg-white/5 transition-colors"
            >
              <div className="flex items-center gap-3">
                <MessageCircle size={18} className="text-[#00d4ff]" />
                <span className="font-mono text-sm">WhatsApp</span>
              </div>
              <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/50 group-hover:text-[#00d4ff]">
                chat
              </span>
            </a>

            <a
              href="#"
              className="group flex items-center justify-between gap-4 p-4 border border-white/20 hover:border-[#ff006e] hover:bg-white/5 transition-colors"
            >
              <div className="flex items-center gap-3">
                <Calendar size={18} className="text-[#ff006e]" />
                <span className="font-mono text-sm">{t.contact.book_call}</span>
              </div>
              <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/50 group-hover:text-[#ff006e]">
                calendly
              </span>
            </a>
          </div>
        </div>

        <div className="lg:col-span-7">
          {done ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              data-testid={CONTACT.success}
              className="border border-[#00d4ff] p-10 flex flex-col items-start"
            >
              <div className="w-12 h-12 border border-[#00d4ff] flex items-center justify-center mb-6">
                <Check size={20} className="text-[#00d4ff]" />
              </div>
              <h3 className="font-mono text-xl">{t.contact.sent}</h3>
            </motion.div>
          ) : (
            <form onSubmit={submit} data-testid={CONTACT.form} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Input
                tid={CONTACT.name}
                label={t.contact.name}
                value={form.name}
                onChange={(v) => setForm({ ...form, name: v })}
                required
              />
              <Input
                tid={CONTACT.email}
                label={t.contact.email}
                type="email"
                value={form.email}
                onChange={(v) => setForm({ ...form, email: v })}
                required
              />
              <Select
                tid={CONTACT.type}
                label={t.contact.project_type}
                options={t.contact.types}
                value={form.project_type}
                onChange={(v) => setForm({ ...form, project_type: v })}
              />
              <Select
                tid={CONTACT.budget}
                label={t.contact.budget}
                options={t.contact.budgets}
                value={form.budget}
                onChange={(v) => setForm({ ...form, budget: v })}
              />
              <div className="sm:col-span-2">
                <Textarea
                  tid={CONTACT.message}
                  label={t.contact.message}
                  value={form.message}
                  onChange={(v) => setForm({ ...form, message: v })}
                  required
                />
              </div>
              {error && <div className="sm:col-span-2 text-[#ff006e] font-mono text-xs">{error}</div>}
              <div className="sm:col-span-2 flex justify-end">
                <button
                  type="submit"
                  disabled={loading}
                  data-testid={CONTACT.submit}
                  className="inline-flex items-center gap-3 px-6 py-4 bg-[#00d4ff] text-[#0a0a0a] font-mono text-xs uppercase tracking-[0.2em] hover:bg-white transition-colors disabled:opacity-50"
                >
                  {loading ? t.auth.loading : t.contact.send}
                  <Send size={14} />
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}

const Input = ({ tid, label, type = "text", value, onChange, required }) => (
  <label className="block">
    <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/60 mb-2">{label}</div>
    <input
      data-testid={tid}
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      required={required}
      className="w-full px-4 py-3 bg-transparent border border-white/30 text-white font-sans focus:outline-none focus:border-[#00d4ff] transition-colors"
    />
  </label>
);

const Textarea = ({ tid, label, value, onChange, required }) => (
  <label className="block">
    <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/60 mb-2">{label}</div>
    <textarea
      data-testid={tid}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      required={required}
      rows={5}
      className="w-full px-4 py-3 bg-transparent border border-white/30 text-white font-sans focus:outline-none focus:border-[#00d4ff] resize-none transition-colors"
    />
  </label>
);

const Select = ({ tid, label, value, onChange, options }) => (
  <label className="block">
    <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/60 mb-2">{label}</div>
    <select
      data-testid={tid}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full px-4 py-3 bg-transparent border border-white/30 text-white font-sans focus:outline-none focus:border-[#00d4ff] transition-colors appearance-none"
    >
      <option value="" className="bg-[#0a0a0a]">—</option>
      {options.map((o) => (
        <option key={o} value={o} className="bg-[#0a0a0a]">
          {o}
        </option>
      ))}
    </select>
  </label>
);
